import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../../../../store';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useStyles } from '../Interfaces.style';
import { saveNode } from './Interfaces.api';
import {
  getCollectionConfig,
  getInterfaceTypeConfig,
  FIELD_TYPES,
  serializeFieldValue,
  isFieldVisible,
} from '../Interfaces.config';
import { EMPTY_RULE, cleanSendingRules } from '../Interfaces.helpers';
import SendingRulesEditor from './SendingRulesEditor';

const ROUTING_TAG_MAX_LEN = 64;

const parseHexField = (value) => {
  if (value == null || value === '') return null;
  const text = String(value).trim().toLowerCase();
  const hex = text.startsWith('0x') ? text.slice(2) : text;
  if (!hex || !/^[0-9a-f]+$/.test(hex)) return null;
  return parseInt(hex, 16);
};

const hasActiveRoutingTag = (report) => {
  if (!report) return false;
  if (report.routing_tag_kind === 'standard') {
    const tag = String(report.routing_tag || '').trim();
    return Boolean(tag && tag.length <= ROUTING_TAG_MAX_LEN);
  }
  if (report.routing_tag_kind === 'private') {
    const group = parseHexField(report.routing_tag_group);
    const offset = parseHexField(report.routing_tag_offset);
    const creator = String(report.routing_tag_creator || '').trim();
    return Boolean(
      creator &&
      creator.length <= ROUTING_TAG_MAX_LEN &&
      group > 0 &&
      group <= 0xFFFF &&
      group % 2 === 1 &&
      offset != null &&
      offset >= 0 &&
      offset <= 0xFF
    );
  }
  return false;
};

const toChecked = (value, fallback = true) => {
  if (value === undefined || value === null) return fallback;
  return value === 1 || value === true;
};

const normalizeMode = (mode) => {
  if (!mode) return '';
  const modeLower = String(mode).toLowerCase();
  if (modeLower === 'manual') return 'manual';
  if (modeLower === 'automatic') return 'automatic';
  return modeLower;
};

const resolveTypeId = (node, collection) => {
  if (node?._typeId) return node._typeId;
  if (node?._submoduleId) return node._submoduleId;
  if (collection?.allowTypeSelection && node?.url) return 'fhir';
  if (collection?.allowTypeSelection && node?.ip) return 'hl7';
  return collection?.defaultTypeId || collection?.typeIds?.[0] || null;
};

// Component for creating/editing Interface nodes
const CreateNodeDialog = ({
  open,
  onClose,
  onSaved,
  initialData,
  moduleId,
  collectionId,
  submoduleId,
  setReportRulesChanged,
  renderCustomFields,
}) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const classes = useStyles();
  const institutionList = useSelector((state) => state.operators_devices.institution_list) || [];
  const globalSettings = useSelector((state) => state.global_settings);

  const resolvedCollectionId = collectionId || submoduleId;
  const collection = getCollectionConfig(moduleId, resolvedCollectionId);

  const [selectedTypeId, setSelectedTypeId] = useState(() => resolveTypeId(initialData, collection));
  const config = getInterfaceTypeConfig(selectedTypeId);
  const [form, setForm] = useState(() => ({ ...(config?.emptyNode || {}), institutionSearch: '' }));

  useEffect(() => {
    if (open) {
      setSelectedTypeId(resolveTypeId(initialData, collection));
    }
  }, [open, initialData, collection]);

  useEffect(() => {
    if (!open || !config) return;

    if (initialData) {
      const formData = { ...config.emptyNode };
      config.fields.forEach((field) => {
        if (field.key in initialData) {
          if (field.type === FIELD_TYPES.NUMBER) {
            formData[field.key] = String(initialData[field.key] || '');
          } else if (field.type === FIELD_TYPES.BOOLEAN) {
            formData[field.key] = toChecked(initialData[field.key], false);
          } else if (field.type === FIELD_TYPES.AUTOCOMPLETE && field.key === 'institutions') {
            formData[field.key] = Array.isArray(initialData[field.key]) ? initialData[field.key] : [];
          } else if (field.type === FIELD_TYPES.SELECT) {
            formData[field.key] =
              initialData[field.key] !== undefined ? initialData[field.key] : field.options?.[0]?.value;
          } else {
            formData[field.key] = initialData[field.key] || '';
          }
        }
      });

      if (config.customFields) {
        config.customFields.forEach((customField) => {
          if (customField === 'sending_rules' && initialData.sending_rules) {
            formData.sending_rules =
              initialData.sending_rules.length > 0
                ? initialData.sending_rules.map((rule) => ({
                    type: rule.type || '',
                    subtype: rule.subtype || '',
                    mode: normalizeMode(rule.mode),
                    vendor: rule.vendor || '',
                    modality_tag: rule.modality_tag || '',
                    dark_mode: rule.dark_mode === true || rule.dark_mode === 1,
                    monochrome: rule.monochrome === true || rule.monochrome === 1,
                    is_active: rule.is_active,
                  }))
                : [EMPTY_RULE];
          } else if (initialData[customField]) {
            formData[customField] = initialData[customField];
          }
        });
      }

      const institutionsField = config.fields.find((f) => f.key === 'institutions');
      if (institutionsField) {
        formData.institutions = Array.isArray(initialData.institutions) ? initialData.institutions : [];
        formData.institutionSearch = '';
      }
      if (initialData.routing_mode) {
        formData.routing_mode = initialData.routing_mode === 'custom' ? 'custom' : 'institution';
      }

      setForm(formData);
    } else {
      setForm({ ...config.emptyNode, institutionSearch: '' });
    }
  }, [initialData, open, config, selectedTypeId]);

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const saveServer = async () => {
    if (!config) return;
    if (setReportRulesChanged) setReportRulesChanged(true);

    const payload = {};

    config.fields.forEach((field) => {
      if (!isFieldVisible(field, form)) return;
      payload[field.key] = serializeFieldValue(field, form[field.key]);
    });

    if (config.preparePayload) {
      config.preparePayload(payload, form);
    }

    if (config.id === 'sending') {
      payload.routing_mode = form.routing_mode === 'custom' ? 'custom' : 'institution';
    }

    if (config.customFields) {
      config.customFields.forEach((customField) => {
        if (customField === 'sending_rules') {
          payload.sending_rules = cleanSendingRules(form.sending_rules, { isNew: !initialData });
        } else if (form[customField] !== undefined) {
          payload[customField] = form[customField];
        }
      });
    }

    const res = await saveNode(config.id, payload);
    dispatch(
      actions.setSnackbar({
        msg: res.success ? t('Settings.saved_successfully') : res.msg,
        severity: res.success ? 'success' : 'error',
      }),
    );

    if (res.success) {
      onClose?.();
      onSaved?.(resolvedCollectionId);
    }
  };

  const canSave =
    !!config &&
    config.fields.every((field) => {
      if (!isFieldVisible(field, form)) return true;
      if (!field.required) return true;
      const value = form[field.key];

      if (field.type === FIELD_TYPES.NUMBER) {
        return value !== undefined && value !== '' && Number(value) > 0;
      }

      if (field.type === FIELD_TYPES.BOOLEAN) {
        return value !== undefined && value !== null;
      }

      if (field.type === FIELD_TYPES.SELECT) {
        return value !== undefined && value !== '';
      }

      if (field.type === FIELD_TYPES.AUTOCOMPLETE) {
        return Array.isArray(value) ? value.length > 0 : Boolean(value);
      }

      return value && String(value).trim().length > 0;
    });

  const renderField = (field) => {
    const value = form[field.key];

    switch (field.type) {
      case FIELD_TYPES.TEXT:
      case FIELD_TYPES.URL:
        return (
          <TextField
            size='small'
            variant='outlined'
            label={t(field.labelKey)}
            value={value || ''}
            onChange={(e) => updateForm(field.key, e.target.value)}
            className={classes.textField}
            fullWidth
            required={field.required}
            placeholder={
              field.placeholderFrom ? globalSettings?.[field.placeholderFrom] || '' : field.placeholder || ''
            }
          />
        );

      case FIELD_TYPES.NUMBER:
        return (
          <TextField
            size='small'
            variant='outlined'
            label={t(field.labelKey)}
            value={value || ''}
            onChange={(e) => updateForm(field.key, e.target.value)}
            className={classes.textField}
            fullWidth
            type='number'
            required={field.required}
          />
        );

      case FIELD_TYPES.BOOLEAN:
        return (
          <FormControlLabel
            control={
              <Switch
                checked={toChecked(value, false)}
                onChange={(e) => updateForm(field.key, e.target.checked ? 1 : 0)}
                color='primary'
              />
            }
            label={t(field.labelKey)}
          />
        );

      case FIELD_TYPES.SELECT:
        if (field.renderAs === 'toggle') {
          const dailyOption = field.options?.find((option) => option.value === 'daily');
          const intervalOption = field.options?.find((option) => option.value === 'interval');
          const isInterval = value === 'interval';
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 8 }}>
              <Typography variant='body2'>{t(field.descriptionKey)}</Typography>
              <Typography
                variant='body2'
                style={{ color: !isInterval ? undefined : '#9e9e9e', fontWeight: !isInterval ? 600 : 400 }}>
                {t(dailyOption?.labelKey)}
              </Typography>
              <Switch
                checked={isInterval}
                onChange={(e) => updateForm(field.key, e.target.checked ? 'interval' : 'daily')}
                color='primary'
              />
              <Typography
                variant='body2'
                style={{ color: isInterval ? undefined : '#9e9e9e', fontWeight: isInterval ? 600 : 400 }}>
                {t(intervalOption?.labelKey)}
              </Typography>
            </div>
          );
        }

        const firstOptionValue = field.options?.[0]?.value;
        const selectValue = value ?? firstOptionValue ?? '';
        const normalizedSelectValue =
          typeof firstOptionValue === 'number' ? Number(selectValue) : selectValue;

        return (
          <FormControl
            fullWidth
            variant='outlined'
            size='small'
            style={{ marginLeft: '8px', maxWidth: '90%' }}>
            <InputLabel>{t(field.labelKey)}</InputLabel>
            <Select
              value={normalizedSelectValue}
              onChange={(e) => updateForm(field.key, e.target.value)}
              label={t(field.labelKey)}>
              {(field.options || []).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </MenuItem>
              ))}
            </Select>
            {field.descriptionKey && <FormHelperText>{t(field.descriptionKey)}</FormHelperText>}{' '}
          </FormControl>
        );

      case FIELD_TYPES.PASSWORD:
        return (
          <TextField
            size='small'
            variant='outlined'
            label={t(field.labelKey)}
            value={value || ''}
            onChange={(e) => updateForm(field.key, e.target.value)}
            className={classes.textField}
            fullWidth
            type='password'
            required={field.required}
          />
        );

      case FIELD_TYPES.AUTOCOMPLETE: {
        const customRouting = form.routing_mode === 'custom';
        const showRoutingMode = config.id === 'sending';
        const routingTagReady = hasActiveRoutingTag(globalSettings?.report_configuration);
        return (
          <div className={classes.institutionsContainer}>
            <div className={classes.institutionsHeaderContainer}>
              <Typography className={classes.listHeaderName}>
                {t(customRouting ? 'Settings.routing_values' : 'Settings.institutions_served')}
              </Typography>
              <Typography
                style={{
                  fontSize: '12px',
                  color: '#c1c1c1',
                  textAlign: 'right',
                  width: '50%',
                  fontStyle: 'italic',
                }}>
                {t(customRouting ? 'Settings.custom_routing_helper' : 'Settings.all_institutions_helper')}
              </Typography>
            </div>

            {showRoutingMode && (
              <>
                <FormControlLabel
                  style={{ padding: '0 16px' }}
                  control={
                    <Checkbox
                      checked={customRouting}
                      disabled={!customRouting && !routingTagReady}
                      onChange={(e) => {
                        updateForm('routing_mode', e.target.checked ? 'custom' : 'institution');
                        updateForm('institutions', []);
                        updateForm('institutionSearch', '');
                      }}
                      name='routing_mode'
                    />
                  }
                  label={t('Settings.custom_routing')}
                />
                {!routingTagReady && (
                  <Typography style={{ fontSize: '12px', color: '#c1c1c1', padding: '0 16px 8px' }}>
                    {t('Settings.custom_routing_requires_tag')}
                  </Typography>
                )}
              </>
            )}

            <Grid container spacing={1} style={{ padding: '16px' }}>
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth={true}
                  multiple={field.multiple}
                  freeSolo={field.freeSolo}
                  disableCloseOnSelect={field.multiple}
                  options={customRouting ? [] : institutionList.map((i) => i.institution)}
                  value={form.institutions || []}
                  inputValue={form.institutionSearch || ''}
                  onInputChange={(e, inputValue) => {
                    updateForm('institutionSearch', inputValue || '');
                  }}
                  onChange={(e, selectedValue) => {
                    updateForm('institutions', selectedValue);
                    updateForm('institutionSearch', '');
                  }}
                  filterOptions={(options, state) => {
                    const input = state.inputValue;
                    const filtered = customRouting
                      ? []
                      : options.filter((o) => o.toLowerCase().includes(input.toLowerCase()));
                    if (input.length >= (customRouting ? 1 : 2) && !options.some((o) => o.toLowerCase() === input.toLowerCase())) {
                      filtered.push(state.inputValue);
                    }
                    return filtered;
                  }}
                  renderOption={(option, state) => (
                    <>
                      {field.multiple && <Checkbox checked={state.selected} style={{ marginRight: 8 }} />}
                      {option}
                    </>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      size='small'
                      label={t(customRouting ? 'Settings.routing_values' : field.labelKey)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (!collection || !config) return null;

  const headerToggles = config.headerToggles || [];
  const editableFields = config.fields.filter((field) => !headerToggles.includes(field.key));
  const typeOptions = collection.typeIds || [];
  const showTypeSelection = collection.allowTypeSelection && typeOptions.length > 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={'md'} fullWidth>
      <DialogTitle>
        <div className={classes.dialogTitleContainer}>
          <Typography variant='h6' color='primary'>
            {t(config.dialogTitleKey || 'Settings.node_config')}
          </Typography>
          <Typography variant='body2' style={{ color: '#c1c1c1' }}>
            {t(config.dialogSubtitleKey || 'Settings.node_config_subtitle')}
          </Typography>
        </div>
      </DialogTitle>

      <DialogContent>
        {showTypeSelection && (
          <div style={{ marginBottom: 24 }}>
            <FormControl fullWidth variant='outlined' size='small'>
              <InputLabel>{t('Settings.report_type')}</InputLabel>
              <Select
                value={selectedTypeId || ''}
                onChange={(e) => setSelectedTypeId(e.target.value)}
                label={t('Settings.report_type')}
                disabled={!!initialData}>
                {typeOptions.map((typeId) => {
                  const typeConfig = getInterfaceTypeConfig(typeId);
                  return (
                    <MenuItem key={typeId} value={typeId}>
                      {typeConfig?.typeLabel ||
                        (typeConfig ? t(typeConfig.titleKey, typeConfig.typeLabel) : typeId)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
        )}

        <div className={classes.modulesContainer}>
          <div className={classes.listHeaderContainer}>
            <Typography className={classes.listHeaderName}>{t('Settings.node_details')}</Typography>
            {headerToggles.length > 0 && (
              <div style={{ display: 'flex', gap: '16px', marginLeft: 'auto' }}>
                {headerToggles.map((fieldKey) => {
                  const field = config.fields.find((f) => f.key === fieldKey);
                  if (!field) return null;

                  return (
                    <FormControlLabel
                      key={fieldKey}
                      control={
                        <Switch
                          checked={toChecked(form[fieldKey])}
                          onChange={(e) => updateForm(fieldKey, e.target.checked ? 1 : 0)}
                          color='primary'
                        />
                      }
                      label={t(field.labelKey)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <Grid
            container
            spacing={4}
            style={{
              height: '100%',
              minHeight: '170px',
            }}>
            {editableFields.map((field) => {
              if (!isFieldVisible(field, form)) return null;

              return (
                <Grid item xs={12} md={field.type === FIELD_TYPES.AUTOCOMPLETE ? 12 : 6} key={field.key}>
                  {renderField(field)}
                </Grid>
              );
            })}
          </Grid>

          {config.customFields?.includes('sending_rules') && (
            <SendingRulesEditor
              form={form}
              updateForm={updateForm}
              initialData={initialData}
              classes={classes}
              t={t}
            />
          )}

          {renderCustomFields && renderCustomFields(form, updateForm, classes, t)}
        </div>
      </DialogContent>

      <DialogActions style={{ margin: '0 22px' }}>
        <Button onClick={onClose} size='small'>
          {t('Settings.cancel')}
        </Button>
        <Button
          onClick={saveServer}
          color='primary'
          disabled={!canSave || (showTypeSelection && !selectedTypeId)}
          variant={canSave && (!showTypeSelection || selectedTypeId) ? 'contained' : 'outlined'}
          size='small'>
          {t('Settings.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateNodeDialog;
